import { draftTenths } from './shot-entry';

export interface ScoringTeam {
  teamId?: string;
  name: string;
  members?: readonly { shooterId: string }[];
}

/** A zero is a saved miss; a legacy whole-series total is not a completed shot grid. */
export function completedShooters(
  scores: readonly { shooterId: string; shotNumber: number; pointsDisplay: string }[],
  shotsPlanned: number,
): Set<string> {
  const entered = new Map<string, Set<number>>();
  for (const score of scores) {
    if (score.shotNumber < 1 || score.shotNumber > shotsPlanned || draftTenths(score.pointsDisplay) == null) continue;
    const shots = entered.get(score.shooterId) ?? new Set<number>();
    shots.add(score.shotNumber);
    entered.set(score.shooterId, shots);
  }
  return new Set([...entered].filter(([, shots]) => shots.size === shotsPlanned).map(([id]) => id));
}

/** Completion belongs to the selected team, including every actual roster member. */
export function completedScoringTeam(
  teams: readonly ScoringTeam[],
  currentId: string,
  complete: ReadonlySet<string>,
): ScoringTeam | null {
  const team = teams.find((entry) => entry.teamId && entry.members?.some((member) => member.shooterId === currentId));
  return team?.members?.length && team.members.every((member) => complete.has(member.shooterId)) ? team : null;
}

/** Teams follow stable roster order, never the changing team leaderboard ranks. */
export function nextTeamShooter<T extends { id: string }>(
  roster: readonly T[],
  teams: readonly ScoringTeam[],
  currentId: string,
  complete: ReadonlySet<string>,
): { shooter: T; team: ScoringTeam | null; sameTeam: boolean } | null {
  const byShooter = new Map<string, ScoringTeam>();
  for (const team of teams) {
    if (!team.teamId) continue;
    for (const member of team.members ?? []) byShooter.set(member.shooterId, team);
  }
  const currentTeam = byShooter.get(currentId);
  if (!currentTeam) return null;
  const teammates = roster.filter((s) => byShooter.get(s.id)?.teamId === currentTeam.teamId);
  const index = teammates.findIndex((s) => s.id === currentId);
  if (index < 0) return null;
  const following = [...teammates.slice(index + 1), ...teammates.slice(0, index)];
  const teammate = following.find((s) => !complete.has(s.id));
  if (teammate) return { shooter: teammate, team: currentTeam, sameTeam: true };
  return null;
}
